import {
  defineElement,
  debounce,
  RESIZE_DEBOUNCE_TIMER,
  gsapWithCSS,
} from "./gw.js";
import { token } from "./helpers.js";
class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.itemID = this.getAttribute("item-id") || null;
    this.handleClick = this.toggle.bind(this);
    this.handleStart = this.onStart.bind(this);
    this.handleComplete = this.onComplete.bind(this);
    this.handleReverseComplete = this.onReverseComplete.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.handleOpenAttributes = this.updateAttributes.bind(this, true);
    this.handleCloseAttributes = this.updateAttributes.bind(this, false);
    this.handleResize = debounce(
      this.onResize.bind(this),
      RESIZE_DEBOUNCE_TIMER,
    );
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }
  /**
   * Initializes the accordion.
   * @param {boolean} initObserver Whether to initialize the observer.
   */
  init(initObserver = true) {
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.content = this.summary.nextElementSibling;
    if (this.group) {
      this.siblings = Array.from(
        this.container.querySelectorAll(
          `accordion-item[group="${this.group}"]`,
        ),
      ).filter((child) => child !== this);
    } else if (this.closest(".accordion-group__list")) {
      this.siblings = Array.from(
        this.container.querySelectorAll("accordion-item"),
      ).filter((child) => child !== this);
    }
    this.isFirst = this.container.children[0] === this;
    this.defaultOpenAt = this.getAttribute("default-open-at");
    this.lockOpenState = this.hasAttribute("lock-open-state");
    this.observeResize = this.hasAttribute("observe-resize");
    this.setupAccessibility();
    this.buildTimeline();
    this.summary.addEventListener("click", this.handleClick);
    window.addEventListener("resize", this.handleResize);
    this.checkUrlHash();
    if (this.isOpen()) this.open(false, true);
    this.initBreakpoint();
    if (initObserver) this.initObserver();
  }
  /**
   * Sets up accessibility attributes for the accordion.
   */
  setupAccessibility() {
    if (!this.content.id) {
      const uniqueId = `accordion-content-${Math.random().toString(36).substring(2, 9)}`;
      this.content.id = uniqueId;
    }
    this.summary.setAttribute("aria-controls", this.content.id);
  }

  initBreakpoint() {
    if (!this.defaultOpenAt) return;
    const tokens = token();
    const breakpoint = tokens.breakpoint[this.defaultOpenAt];
    if (breakpoint) {
      this.mm = window.matchMedia(`(min-width: ${breakpoint}px)`);
      this.mm.addEventListener("change", this.onBreakpointChange);
      this.toggleBreakpoint(this.mm.matches);
    }
  }

  onBreakpointChange(e) {
    this.toggleBreakpoint(e.matches);
  }

  toggleBreakpoint(matches) {
    if (matches) {
      this.open();
    } else {
      this.close();
    }
  }
  /**
   * The group that the accordion item belongs to.
   * @type {string}
   */
  get group() {
    return this.getAttribute("group") || null;
  }
  /**
   * The element that contains the accordion items.
   * @type {Element}
   */
  get container() {
    return (
      this.closest(this.getAttribute("group-container")) ??
      this.closest(".accordion-group__list") ??
      this.parentElement
    );
  }
  /**
   * The accordion items that belong to the same group.
   * @type {AccordionItem[]}
   */
  get slaves() {
    return Array.from(
      document.querySelectorAll(`accordion-item[item-id="${this.itemID}"]`),
    );
  }
  /**
   * Whether the accordion item should observe changes to its group container.
   * @type {boolean}
   */
  get observe() {
    const attr = this.getAttribute("observe");
    return attr !== void 0 && attr !== "false";
  }
  /**
   * Sets the group that the accordion item belongs to.
   * @param {string} val The group name.
   */
  set group(val) {
    if (val) {
      this.setAttribute("group", val);
    } else {
      this.removeAttribute("group");
    }
  }
  /**
   * Sets whether the accordion item should observe changes to its group container.
   * @param {boolean} val Whether to observe changes.
   */
  set observe(val) {
    if (val) {
      this.setAttribute("observe", val);
    } else {
      this.removeAttribute("observe");
    }
  }
  /**
   * Initializes the observer for the group container.
   */
  initObserver() {
    if (!this.observe) return;
    this.observer = new MutationObserver((mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          this.reset();
        }
      }
    });
    this.observer.observe(this.details, {
      childList: true,
    });
  }
  /**
   * Creates the timeline for opening and closing the accordion.
   * @returns {GSAPTimeline} The timeline.
   */
  buildTimeline() {
    this.timeline = gsapWithCSS
      .timeline({
        paused: true,
        onStart: this.handleStart,
        onReverseComplete: this.handleCloseAttributes,
        // onStart: this.handleStart,
        // onComplete: this.handleComplete,
        // onReverseComplete: this.handleReverseComplete,
      })
      .fromTo(
        this.content,
        {
          height: 0,
          autoAlpha: 0,
        },
        {
          height: "auto",
          autoAlpha: 1,
          duration: 0.4,
          ease: "power2.inOut",
        },
      );
  }

  onComplete() {}
  onStart() {
    if (!this.timeline.reversed()) {
      this.handleOpenAttributes();
    }
  }

  onReverseComplete() {
    this.handleCloseAttributes();
  }
  /**
   * Checks whether the accordion is open based on the URL hash.
   */
  checkUrlHash() {
    if (!this.details.hasAttribute("id")) return;
    const hash = window.location.hash.replace("#", "");
    if (
      hash === this.details.id.replace("Accordion-", "") ||
      (this.isFirst && hash === this.group)
    ) {
      this.updateAttributes(true);
    }
  }
  /**
   * Destroys the accordion.
   * @param {boolean} destroyObserver Whether to destroy the observer.
   */
  destroy(destroyObserver = true) {
    var _a;
    this.timeline.kill();
    this.summary.removeEventListener("click", this.handleClick);
    window.removeEventListener("resize", this.handleResize);
    if (destroyObserver)
      (_a = this.observer) == null ? void 0 : _a.disconnect();
  }
  /**
   * Resets the accordion.
   */
  reset() {
    this.destroy(false);
    this.init(false);
  }
  /**
   * Checks whether the accordion is open.
   * @returns {boolean} Whether the accordion is open.
   */
  isOpen() {
    return this.details.hasAttribute("open");
  }
  /**
   * Opens the accordion.
   * @param {boolean} closeSiblings Whether to close sibling accordion items.
   * @param {boolean} instant Whether to skip the animation and open the accordion instantly.
   * @param {Object} [options] Options for opening the accordion.
   * @param {boolean} [options.mirror=true] Whether to also open all slave accordions.
   */
  open(closeSiblings = true, instant = false, { mirror = true } = {}) {
    var _a, _b;
    if (mirror && this.itemID) {
      (_a = this.slaves) == null
        ? void 0
        : _a.forEach((slave) =>
            slave.open(closeSiblings, instant, { mirror: false }),
          );
    }
    if (closeSiblings) {
      (_b = this.siblings) == null
        ? void 0
        : _b.forEach((sibling) => sibling.close());
    }
    if (instant) {
      this.timeline.progress(1);
    } else {
      this.timeline.invalidate().play();
    }
  }
  /**
   * Closes the accordion.
   * @param {boolean} instant Whether to close the accordion instantly.
   * @param {Object} [options] Options for closing the accordion.
   * @param {boolean} [options.mirror=true] Whether to also close all slave accordions.
   */
  close(instant = false, { mirror = true } = {}) {
    var _a, _b;
    if (this.lockOpenState && ((_a = this.mm) == null ? void 0 : _a.matches))
      return;
    if (mirror && this.itemID) {
      (_b = this.slaves) == null
        ? void 0
        : _b.forEach((slave) => slave.close(instant, { mirror: false }));
    }
    this.details.classList.remove("is-open");
    if (instant) {
      this.timeline.progress(0);
    } else {
      this.timeline.invalidate().reverse();
    }
  }
  /**
   * Updates the attributes of the accordion based on its open state.
   * @param {boolean} open Whether the accordion is open.
   */
  updateAttributes(open = true) {
    this.details.classList.toggle("is-open", open);
    this.toggleAttribute("open", open);
    this.details.toggleAttribute("open", open);
    this.summary.setAttribute("aria-expanded", open);
  }
  /**
   * Toggles the open state of the accordion.
   * @param {Event} [e] The event that triggered the toggle.
   */
  toggle(e) {
    e == null ? void 0 : e.preventDefault();
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }
  /**
   * Callback that is invoked when the window is resized.
   */
  onResize() {
    var _a;
    (_a = this.timeline) == null ? void 0 : _a.invalidate();
  }
}
defineElement("accordion-item", AccordionItem);
